import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const dogBiteSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/dog-bite#webpage`,
      url: `${siteUrl}/dog-bite`,
      name: 'Dog Bite Attorney Indiana | Animal Attack Lawyer Fishers | Jun Law Firm',
      description:
        'Attacked by a dog in Indiana? Dog owners are strictly liable under Indiana law. Jun Law Firm fights for victims to recover full compensation for injuries, scarring, and trauma. Free case review. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/dog-bite#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Dog Bite Attorney', item: `${siteUrl}/dog-bite` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/dog-bite#service`,
      name: 'Jun Law Firm - Dog Bite Attorney Indiana',
      url: `${siteUrl}/dog-bite`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Indiana dog bite and animal attack attorney representing victims under Indiana strict liability law. Recover compensation for medical expenses, scarring, trauma, and lost wages. Free case review.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is Indiana a strict liability state for dog bites?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Indiana follows strict liability for dog bite injuries. You do not need to prove the owner knew the dog was dangerous — you only need to prove the bite occurred and caused your injuries.',
          },
        },
        {
          '@type': 'Question',
          name: 'What compensation can I recover from a dog bite in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You may recover medical expenses, surgery and reconstruction costs, lost wages, emotional trauma, pain and suffering, and compensation for permanent scarring or disfigurement.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I do right after being bitten by a dog?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Seek medical attention immediately, report the incident to local animal control, document your injuries with photos, obtain the dog owner\'s information and any witness contacts, and consult a dog bite attorney promptly.',
          },
        },
      ],
    },
  ],
};

export default function DogBite() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const injuryTypes = [
    {
      number: 'I',
      title: 'Puncture Wounds',
      description: 'Deep bites that penetrate muscle and tissue, often requiring surgery and carrying high risk of serious infection.'
    },
    {
      number: 'II',
      title: 'Nerve & Tissue Damage',
      description: 'Severe attacks that damage nerves, tendons, and underlying tissue, potentially causing permanent loss of function.'
    },
    {
      number: 'III',
      title: 'Scarring & Disfigurement',
      description: 'Facial and body scarring that may require reconstructive surgery and causes lasting psychological trauma.'
    }
  ];

  const compensationAreas = [
    {
      category: 'Medical Expenses',
      examples: ['Emergency room treatment', 'Surgery and hospitalization', 'Reconstructive procedures', 'Ongoing wound care']
    },
    {
      category: 'Emotional Trauma',
      examples: ['PTSD and anxiety disorders', 'Fear of animals', 'Therapy and counseling', 'Sleep disturbances']
    },
    {
      category: 'Lost Income',
      examples: ['Time off work during recovery', 'Reduced earning capacity', 'Future lost wages', 'Career impact']
    },
    {
      category: 'Disfigurement',
      examples: ['Permanent scarring', 'Facial injuries', 'Reconstructive surgery costs', 'Pain and suffering']
    },
    {
      category: 'Children\'s Injuries',
      examples: ['Child-specific bite injuries', 'Developmental trauma', 'School absence recovery', 'Long-term care']
    },
    {
      category: 'Fatal Attacks',
      examples: ['Wrongful death claims', 'Funeral expenses', 'Survivor compensation', 'Family support losses']
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Dog Bite Attorney Indiana | Animal Attack Lawyer Fishers | Jun Law Firm"
        description="Attacked by a dog in Indiana? Dog owners are strictly liable under Indiana law. Jun Law Firm fights for victims to recover full compensation for injuries, scarring, and trauma. Free case review. (317) 701-2768."
        keywords="dog bite attorney Indiana, animal attack lawyer Fishers, dog bite injury lawyer Indiana, strict liability dog bite attorney, dog attack compensation Indiana"
        canonicalPath="/dog-bite"
        schema={dogBiteSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Animal Attack Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Dog Bite <em className="font-light italic">Attorney</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Attacked by a dog in Indiana? Under Indiana law, dog owners are strictly liable for injuries caused by their animals. Jun Law Firm fights for victims — including immigrant communities — to recover full compensation for medical costs, scarring, trauma, and lost wages.
            </p>
            <div className="flex gap-6">
              <button
                onClick={() => {
                  const element = document.getElementById('contact-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Free Case Review
              </button>
              <a
                href="tel:+13177012768"
                className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Call: (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Injury Types */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Types of Harm
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Serious Injuries We <em className="italic">Represent</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Dog bites cause far more than physical wounds — they leave lasting physical and emotional damage that demands full legal accountability.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {injuryTypes.map((type, index) => (
              <article key={index} className="text-center group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {type.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {type.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {type.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Compensation Areas */}
      <section className="py-24 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              What You Can Recover
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Full <em className="italic">Compensation</em> Available
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Indiana's strict liability law means you don't have to prove the owner knew the dog was dangerous — you just need to prove the bite happened.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {compensationAreas.map((area, index) => (
              <article key={index} className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {area.category}
                </h3>
                <ul className="space-y-2">
                  {area.examples.map((example, idx) => (
                    <li key={idx} className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                      — {example}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Our Approach
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              How We <em className="italic">Build</em> Your Case
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Dog bite cases move fast — evidence disappears and witnesses forget. Our team acts immediately to protect your rights and build the strongest possible claim.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <article className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>01</div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>Immediate Investigation</h3>
              <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                We gather incident reports, witness statements, medical records, and evidence of prior attacks to establish owner liability.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>02</div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>Insurance Negotiation</h3>
              <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                We deal directly with homeowner&apos;s and renter&apos;s insurance companies so you don&apos;t have to navigate complex claims alone.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>03</div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>Medical Documentation</h3>
              <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                We work with doctors to fully document your injuries, future treatment needs, and the long-term impact on your quality of life.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>04</div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>Aggressive Litigation</h3>
              <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                When insurers refuse to pay fair value, we take your case to court and fight for every dollar you deserve.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-24 bg-lavender-100 border-t border-lavender-200" style={{ borderColor: '#ddd8f5' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-gray-400 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
            Begin Your Consultation
          </div>
          <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            We Are Here to <em className="italic">Help</em>
          </h2>
          <div className="w-16 h-px bg-gray-400 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
            After a dog attack, you deserve compassionate guidance and experienced counsel. Contact Jun Law Firm today for a free, confidential case evaluation — in your language.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="tel:+13177012768"
              className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Call: (317) 701-2768
            </a>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }, 300);
              }}
              className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Online Contact Form
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
